import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Grid from "@mui/material/Grid2";
import { Badge, Box, Dialog, Fade, IconButton, LinearProgress, Link, Paper, Typography } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { isEmpty } from "lodash";

import {
  CustomMenu,
  CustomMenuItem,
  CustomMenuItemBox,
  CustomTooltip,
  FaCircleInfoIcon,
  FaCircleUserIcon,
  FaCircleXmarkIcon,
  FaHeadsetIcon,
  HeaderToolbar,
  LogoImg,
  LogoLink,
  ParentMenuItemBox,
  PortalUpdate,
  PortalUpdateClose,
  TriangleIndicator,
  UpdateTextStyle,
  UserDetails,
  UserDetailsGridContainer,
  UserDetailsText,
  UserDetailsTextGrid,
  UserDetailsTextStyle,
  UserDetailsTitle,
  MenuGrid,
  FaRegBellIcon,
  FaBookIcon,
  NotificationListBox,
  NotificationHeaderGridContainer,
  NotificationListBoxSection,
  RenderFieldValueStyle,
  ProgressBarStyle,
  IconFaTimesCircle,
  ModalIconBtnStyle,
  ModalCloseIcon,
  DialogStyle,
} from "../styles/styles";
import { height100PCT } from "../../styles/mui/styles/heights";
import { flexAlignCentre } from "../../styles/mui/styles/display";
import { tangoLogo } from "../../constants/ImagePaths";
import fontWeight from "../../styles/mui/fontWeight";
import {
  ApplicationRoute,
  ApplicationURL,
} from "../../constants/ApplicationUrl";
import {
  ApplicationTitles,
  HeaderRightMenu,
  MenuItemType,
  MenuType,
  ResetAuthReAuthTypes,
} from "../../constants/HomePage";
import { LoggedInUserResponse } from "../../models/api/User";
import { AppConfigs } from "../../models/AppConfigs";
import { colors } from "../../styles/colors";
import { TangoLogoAltText } from "../../constants/User";
import { PortalUpdateData } from "../../models/User";
import { getValue } from "../../utils";
import { formatDate, formatUTCToLocal } from "../../utils/DateTime";
import { DateFormats } from "../../constants/Date";
import { redirectUri } from "../../constants/Config";
import { resetStates } from "../../actions/Common";
import { ListOfNotifications, ViewedNotificationDetails } from "../../models/Referral";
import { DownloadDocumentOfNotificationResponse } from "../../models/api/Referral";
import { DocumentNameUnderline } from "../../routes/HomeScreen/style/style";
import PdfViewer from "../containers/PdfViewer";
import CentralLoader from "./CentralLoader";

export interface PropsFromState {
  config: AppConfigs;
  loggedInUser: LoggedInUserResponse;
  portalUpdateState: PortalUpdateData;
  headerNotificationListState: ListOfNotifications;
  downloadDocumentOfNotificationState: DownloadDocumentOfNotificationResponse;
  userLogoutState: any;
}

export interface PropsFromDispatch {
  getConfig: (file: string) => void;
  getPortalUpdate: () => void;
  getNotificationsList: (userId: string) => void;
  postViewedNotificationDetails: (payload: ViewedNotificationDetails) => void;
  setIsPdfViewerOpen?: (value: boolean) => void;
  setNotificationBlobId?: (blobId: number | null) => void;
  logoutPortalApp: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const Header: React.FC<AllProps> = ({
  loggedInUser,
  portalUpdateState,
  getPortalUpdate,
  getNotificationsList,
  headerNotificationListState,
  postViewedNotificationDetails,
  downloadDocumentOfNotificationState,
  logoutPortalApp,
  userLogoutState
}) => {
  const navigate = useNavigate();
  const [updateStatusWindow, setUpdateStatusWindow] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { instance } = useMsal();
  const [showNotificationsPanel, setShowNotificationsPanel] = useState<boolean>(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState<boolean>(false);
  const [selectedBlobId, setSelectedBlobId] = useState<number | null>(null);
  const [fileName] = useState("");
  const { progress: documentDownloadProgress } = downloadDocumentOfNotificationState;
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    getPortalUpdate();
    getNotificationsList(getValue(loggedInUser,"response.providerPortalUser.userId"));
  }, []);

  const openMenuItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const Logout = async () => {
    logoutPortalApp();
  };
  useEffect(() => {
    const handleLogoutRedirect = async () => {
      if (userLogoutState && getValue(userLogoutState, "response.status") === 200) {
        try {
          sessionStorage.clear();
          await instance.logoutRedirect({
            postLogoutRedirectUri: redirectUri,
          });
        } catch (error) {
          console.error(error);
        }
      }
    };

    handleLogoutRedirect();
  }, [userLogoutState]);

  const menuItem = () => {
    const handleMenuItemClick = (path: string) => {
      path === ApplicationRoute.LOGOUT ? Logout() : navigate(path);
      handleClose();
    };

    const filterMenuByRole = () => {
      if (loggedInUser?.response?.providerPortalUser?.roleType) {
        const { roleType } = loggedInUser.response.providerPortalUser;
        return HeaderRightMenu.filter((item: MenuType) =>
          item.role.includes(roleType)
        );
      }
      return HeaderRightMenu;
    };

    const clickMenuItem = (item: MenuItemType) => {
      Promise.all(
        ResetAuthReAuthTypes.map((actionType) => resetStates(actionType)),
      ).then(() => {
        handleMenuItemClick(item.path);
      });
    };

    return (
      <Grid container sx={MenuGrid}>
        <TriangleIndicator />
        <CustomMenu
          id="fade-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
        >
          <Box sx={CustomMenuItemBox}>
            <Box sx={ParentMenuItemBox}>
              {filterMenuByRole().map((item: MenuItemType) => (
                <CustomMenuItem
                  onClick={() => clickMenuItem(item)}
                  key={item.path}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight[6]}
                    color={colors.black[1]}
                  >
                    {item.title}
                  </Typography>
                </CustomMenuItem>
              ))}
            </Box>
          </Box>
        </CustomMenu>
      </Grid>
    );
  };

  const redirectFreshDesk = () => {
    window.open(ApplicationURL.FRESH_DESK, "_blank");
  };

  const portalUpdateBanner = () => {
    if (
      updateStatusWindow &&
      !isEmpty(getValue(portalUpdateState, "updateText", ""))
    ) {
      return (
        <Grid container>
          <Grid size={{ xs: 10 }} sx={PortalUpdate}>
            <FaCircleInfoIcon />
            <Typography
              variant="h5"
              fontWeight={fontWeight[6]}
              color={colors.white[1]}
              sx={UpdateTextStyle}
            >
              Latest Update &nbsp;
              {formatDate(
                getValue(portalUpdateState, "dateCreated"),
                DateFormats.MM_DD_YYYY
              )}
              &nbsp;:&nbsp;{getValue(portalUpdateState, "updateText")}{" "}
            </Typography>
          </Grid>
          <Grid size={{ xs: 2 }} sx={PortalUpdateClose}>
            <IconButton onClick={() => setUpdateStatusWindow(false)}>
              <FaCircleXmarkIcon />
            </IconButton>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setShowNotificationsPanel(false);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  const [progress, setProgress] = useState<number | undefined>(0);

  useEffect(() => { 
    setProgress(documentDownloadProgress);
  },[documentDownloadProgress]);

  const handleNotificationClick = async (notificationId: number) => {
    await postViewedNotificationDetails({
      userId: loggedInUser?.response?.providerPortalUser?.userId,
      notificationId
    });

    getNotificationsList(
      getValue(loggedInUser, "response.providerPortalUser.userId")
    );
  }

  const handleNotificationFileNameClick = (
    blobId: number,
    notificationId: number
  ) => {
    handleNotificationClick(notificationId);
    setSelectedBlobId(blobId);
    setIsPdfModalOpen(true);
  };

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setSelectedBlobId(null);
  };

  const handleMarkAllAsRead = async () => {
    await postViewedNotificationDetails({
      userId: getValue(loggedInUser, "response.providerPortalUser.userId"),
    });
    setTimeout(() => {
      getNotificationsList(getValue(loggedInUser, "response.providerPortalUser.userId"));
    }, 500);
  };

  const renderField = (
    label: string,
    value: string,
    isLink?: boolean,
    onClick?: () => void
  ) => (
    <Grid size={{ xs: 12 }}>
      <Typography variant="h5"
        fontWeight={fontWeight[4]}
        color={colors.portalColor[2]}
        pr={"0.5rem"}
      >
        {label}:
      </Typography>
      {isLink ? (
        <Link
          variant="h5"
          fontWeight={fontWeight[4]}
          color={colors.fonts[18]}
          sx={DocumentNameUnderline}
          onClick={onClick}
        >
          {value}
        </Link>
      ) : (
          <Typography
            variant="h5"
            color={colors.portalColor[2]}
            sx={RenderFieldValueStyle}
          >
            {value}
          </Typography>
      )}
    </Grid>
  );

  return (
    <>
    <Dialog
      open={isPdfModalOpen}
      onClose={closePdfModal}
      fullScreen
      sx={DialogStyle}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "0.5rem",
            },
          }
        }}
    >
      <Box sx={ModalIconBtnStyle}>
        <IconButton
          aria-label="Close PDF viewer"
          onClick={closePdfModal}
          sx={ModalCloseIcon}
        >
          <IconFaTimesCircle />
        </IconButton>
        {selectedBlobId && <PdfViewer notificationBlobId={selectedBlobId} />}
      </Box>
      <CentralLoader />
    </Dialog>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Paper elevation={3}>
          {portalUpdateBanner()}
          <HeaderToolbar disableGutters>
            <Grid container sx={height100PCT}>
              <Grid size={{ xs: 12 }}>
                <Box sx={height100PCT}>
                  <Grid container sx={height100PCT}>
                    <Grid sx={flexAlignCentre} size={{ xs: 12 }}>
                      <LogoLink to="/">
                        <LogoImg alt={TangoLogoAltText} src={tangoLogo} />
                      </LogoLink>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
            <Grid container sx={UserDetailsTextGrid}>
              <Typography
                variant="h1"
                fontWeight={fontWeight[4]}
                color={colors.primary.main}
                sx={UserDetailsTitle}
              >
                {ApplicationTitles.PRONET_CONNECT}
              </Typography>
            </Grid>
            <Grid container sx={UserDetailsGridContainer}>
              <Grid size={{ xs: 12 }}>
                <Box sx={height100PCT}>
                  <Grid container sx={height100PCT}>
                    <Grid sx={flexAlignCentre} size={{ xs: 12 }}>
                      <Box ref={notificationRef}>
                        <CustomTooltip title="Notification" arrow>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowNotificationsPanel((prev) => !prev);
                            }}
                          >
                            <Badge
                              badgeContent={
                                headerNotificationListState.badgeCount
                              }
                              color="error"
                            >
                              <FaRegBellIcon />
                            </Badge>
                          </IconButton>
                        </CustomTooltip>
                        {showNotificationsPanel && (
                          <Box
                            sx={NotificationListBox}
                            ref={notificationRef}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Grid
                              container
                              sx={NotificationHeaderGridContainer}
                            >
                              <Typography
                                variant="h4"
                                fontWeight={fontWeight[6]}
                              >
                                Notifications
                              </Typography>
                              <Typography
                                variant="h5"
                                color={colors.fonts[18]}
                                fontWeight={fontWeight[4]}
                                sx={{ cursor: "pointer" }}
                                onClick={handleMarkAllAsRead}
                              >
                                Mark all as read
                              </Typography>
                            </Grid>
                            <Box
                              sx={{ maxHeight: 340, overflowY: "auto", p: 2 }}
                            >
                              {headerNotificationListState.notifications
                                .length === 0 ? (
                                <Typography sx={{ textAlign: "center", mt: 2 }}>
                                  No new notifications.
                                </Typography>
                              ) : (
                                headerNotificationListState.notifications.map(
                                  (notif) => (
                                    <Box
                                      key={notif.notificationId}
                                      sx={{
                                        ...NotificationListBoxSection,
                                        backgroundColor: notif.isRead
                                          ? colors.grey[4]
                                          : colors.backgroundColor[2],
                                      }}
                                      onClick={() => {
                                        if (!notif.isRead) {
                                          handleNotificationClick(
                                            notif.notificationId,
                                          );
                                        }
                                      }}
                                    >
                                      <Box sx={{ mt: 0.5 }}>
                                        <FaBookIcon />
                                      </Box>
                                      <Box sx={{ width: "100%" }}>
                                        <Grid container spacing={0.5}>
                                          {renderField(
                                            "Title",
                                            notif.titleOfNotification,
                                          )}
                                          {renderField(
                                            "Notification Type",
                                            notif.notificationType,
                                          )}
                                          {renderField(
                                            "Upload Date",
                                            formatUTCToLocal(
                                              notif.datePublished,
                                              DateFormats.MM_DD_YYYY,
                                            ),
                                          )}
                                          {renderField(
                                            "Link",
                                            notif.originalFileName,
                                            true,
                                            () =>
                                              handleNotificationFileNameClick(
                                                notif.blobId,
                                                notif.notificationId,
                                              ),
                                          )}
                                          <>
                                            {fileName ===
                                              notif.originalFileName &&
                                              progress &&
                                              progress > 0 &&
                                              progress < 100 && (
                                                <Box sx={ProgressBarStyle}>
                                                  <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{
                                                      flex: 1,
                                                      height: 8,
                                                      borderRadius: 5,
                                                    }}
                                                  />
                                                  <Typography
                                                    variant="caption"
                                                    sx={{ minWidth: 32 }}
                                                  >
                                                    {progress}%
                                                  </Typography>
                                                </Box>
                                              )}
                                          </>
                                        </Grid>
                                      </Box>
                                    </Box>
                                  ),
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <CustomTooltip title="Support" arrow>
                        <IconButton onClick={redirectFreshDesk}>
                          <FaHeadsetIcon />
                        </IconButton>
                      </CustomTooltip>
                      <FaCircleUserIcon />
                      <UserDetails
                        id="demo-customized-button"
                        aria-controls={
                          open ? "demo-customized-menu" : undefined
                        }
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={(e) => openMenuItem(e)}
                      >
                        <UserDetailsText sx={UserDetailsTextStyle(1)}>
                          {loggedInUser.response.providerPortalUser.firstName}{" "}
                          {loggedInUser.response.providerPortalUser.lastName}
                        </UserDetailsText>
                        <UserDetailsText sx={UserDetailsTextStyle(0)}>
                          {loggedInUser.response.providerPortalUser.roleType}
                        </UserDetailsText>
                      </UserDetails>
                      {open && menuItem()}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </HeaderToolbar>
        </Paper>
      </Grid>
    </Grid>
    </>
  );
};

export default Header;
